import { html, nothing } from "lit";
import type { FleetQuestionRequest } from "../controllers/fleet-question.ts";
import { isFleetQuestionDraftComplete } from "../controllers/fleet-question.ts";

export type FleetQuestionInlineProps = {
  request: FleetQuestionRequest;
  draft: Record<string, string[]>;
  freeTextByQuestion: Record<string, string>;
  busy?: boolean;
  error?: string | null;
  otherSession?: boolean;
  onToggleOption: (questionId: string, optionId: string, allowMultiple: boolean) => void;
  onFreeTextChange: (questionId: string, value: string) => void;
  onSubmit: () => void;
};

function formatRemaining(ms: number): string {
  const remaining = Math.max(0, ms);
  const totalSeconds = Math.floor(remaining / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}m`;
}

// Sentinel used by the gateway to mean "no time limit on this
// question" (see fleet-question-manager.ts.create). Match against the
// rendered subtitle / timer only when this fires.
const NO_TIME_LIMIT_SENTINEL = 8_640_000_000_000_000;

/**
 * Inline fleet-question card rendered inside the chat thread (a chat-row,
 * not an overlay). Mirrors `fleet-question-global-prompt` markup so existing
 * CSS rules apply, with the addition of `--inline` modifier classes.
 */
export function renderFleetQuestionInline(props: FleetQuestionInlineProps) {
  if (!props.request) {
    return nothing;
  }
  const hasNoTimeLimit = props.request.expiresAtMs >= NO_TIME_LIMIT_SENTINEL;
  const remainingMs = hasNoTimeLimit
    ? Number.POSITIVE_INFINITY
    : props.request.expiresAtMs - Date.now();
  const remaining = hasNoTimeLimit
    ? "no time limit"
    : remainingMs > 0
      ? `expires in ${formatRemaining(remainingMs)}`
      : "expired";
  const canSubmit =
    isFleetQuestionDraftComplete(props.request, props.draft, props.freeTextByQuestion) &&
    !props.busy;

  const answered = Boolean(props.request.answered);
  const sharedHeader = html`
    <div class="exec-approval-header fleet-question-card__header">
      <div>
        <div class="exec-approval-title fleet-question-card__title">
          ${props.request.title}
        </div>
        <div class="exec-approval-sub fleet-question-card__sub">
          ${
            answered
              ? "Answered · chat below to keep going"
              : hasNoTimeLimit
                ? `${remaining}${
                    props.otherSession ? ` · Session: ${props.request.sessionKey}` : ""
                  }`
                : `${remaining}${
                    props.otherSession ? ` · Session: ${props.request.sessionKey}` : ""
                  }`
          }
        </div>
      </div>
    </div>
  `;

  // Two distinct template structures so Lit treats them as separate
  // template instances (different static-part hash). Otherwise Lit
  // reuses the same DOM tree across renders and never re-applies
  // bindings when only the answered state changes.
  if (answered) {
    return renderAnswered(props, sharedHeader);
  }
  return renderUnanswered(props, sharedHeader, canSubmit, remaining);
}

function renderAnswered(
  props: FleetQuestionInlineProps,
  sharedHeader: import("lit").TemplateResult,
) {
  return html`
    <div
      class="fleet-question-row fleet-question-row--inline fleet-question-row--answered"
      data-role="fleet-question-answered-row"
      data-answered="true"
      role="group"
      aria-live="polite"
      aria-label="Fleet question (answered)"
      data-testid="fleet-question-inline"
    >
      <div
        class="fleet-question-card fleet-question-card--inline fleet-question-card--answered"
        data-answered="true"
      >
        ${sharedHeader}
        <div class="fleet-question-body">
          ${props.request!.questions.map(
            (q) => html`
              <fieldset
                class="fleet-question-fieldset"
                disabled
                data-answered="true"
              >
                <legend class="fleet-question-prompt">${q.prompt}</legend>
                <div class="fleet-question-options" role="group" aria-label=${q.prompt}>
                  ${q.options.map((opt) => {
                    const summaryIds = props.request!.answered!.selectedOptionIds[q.id] ?? [];
                    const selected = summaryIds.includes(opt.id);
                    return html`
                      <label
                        class="fleet-question-option ${
                          selected ? "fleet-question-option--selected" : ""
                        }"
                      >
                        <input
                          type=${q.allowMultiple ? "checkbox" : "radio"}
                          name=${`fleet-inline-q-${props.request!.id}-${q.id}`}
                          .checked=${selected}
                          disabled
                        />
                        <span>${opt.label}</span>
                      </label>
                    `;
                  })}
                </div>
                ${
                  (props.request!.answered!.freeText[q.id] ?? "").length > 0
                    ? html`
                      <label class="fleet-question-other muted">
                        <span>Free-text answer:</span>
                        <input
                          type="text"
                          class="fleet-question-other-input"
                          .value=${props.request!.answered!.freeText[q.id] ?? ""}
                          disabled
                          placeholder="Free-text answer"
                        />
                      </label>
                    `
                    : nothing
                }
              </fieldset>
            `,
          )}
        </div>
        ${props.error ? html`<div class="exec-approval-error">${props.error}</div>` : nothing}
      </div>
    </div>
  `;
}

function renderUnanswered(
  props: FleetQuestionInlineProps,
  sharedHeader: import("lit").TemplateResult,
  canSubmit: boolean,
  remaining: string,
) {
  return html`
    <div
      class="fleet-question-row fleet-question-row--inline"
      data-role="fleet-question-row"
      data-answered="false"
      role="group"
      aria-live="polite"
      aria-label="Fleet question"
      data-testid="fleet-question-inline"
    >
      <div
        class="fleet-question-card fleet-question-card--inline"
        data-answered="false"
      >
        ${sharedHeader}
        <div class="fleet-question-body">
          ${props.request!.questions.map(
            (q) => html`
              <fieldset
                class="fleet-question-fieldset"
                ?disabled=${props.busy}
              >
                <legend class="fleet-question-prompt">${q.prompt}</legend>
                <div class="fleet-question-options" role="group" aria-label=${q.prompt}>
                  ${q.options.map((opt) => {
                    const selected = (props.draft[q.id] ?? []).includes(opt.id);
                    return html`
                      <label
                        class="fleet-question-option ${
                          selected ? "fleet-question-option--selected" : ""
                        }"
                      >
                        <input
                          type=${q.allowMultiple ? "checkbox" : "radio"}
                          name=${`fleet-inline-q-${props.request!.id}-${q.id}`}
                          .checked=${selected}
                          ?disabled=${props.busy}
                          @change=${() =>
                            props.onToggleOption(q.id, opt.id, q.allowMultiple === true)}
                        />
                        <span>${opt.label}</span>
                      </label>
                    `;
                  })}
                </div>
                <label class="fleet-question-other muted">
                  <span>Or write your own answer</span>
                  <input
                    type="text"
                    class="fleet-question-other-input"
                    .value=${props.freeTextByQuestion[q.id] ?? ""}
                    ?disabled=${props.busy}
                    placeholder="Free-text answer"
                    @input=${(e: Event) =>
                      props.onFreeTextChange(q.id, (e.target as HTMLInputElement).value)}
                  />
                </label>
              </fieldset>
            `,
          )}
        </div>
        ${props.error ? html`<div class="exec-approval-error">${props.error}</div>` : nothing}
        <div class="exec-approval-actions fleet-question-card__actions">
          <button
            class="btn primary"
            type="button"
            ?disabled=${!canSubmit}
            @click=${() => props.onSubmit()}
          >
            Continue
          </button>
        </div>
        ${
          props.otherSession
            ? html`
                <div class="exec-approval-sub" style="margin-top: 8px">
                  Answering applies to the session above (may differ from the tab you are viewing).
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
}
